import {
  Box,
  Card,
  CardContent,
  Collapse,
  CollapseProps,
  debounce,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { ChangeEvent, forwardRef, useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { useLoadCardCustomAttributeValues } from 'src/components/CustomAttributes/use-load-card-custom-attribute-values';
import { AutocompleteSearchBar } from 'src/components/SearchBar/autocomplete-search-bar';
import type { SelectMenuOption } from 'src/components/SelectMenu';
import { useResponsiveCardStyles } from 'src/components/SidePanelTabs/shared';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Address, Conversation, mapClientToAddress, useAddress } from 'src/graphql';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods } from 'src/store/modals';
import { useCustomView, useInViewTrigger, useNotifications } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { ClientCard, StyledCardHeader } from './client-card';

interface RecentConversationsProps {
  conversation?: Conversation;
  address: Partial<Address>;
  disabled?: boolean;
}

export const WithCollapse = forwardRef(
  (
    {
      showCollapse,
      children,
      ...props
    }: CollapseProps & {
      showCollapse?: boolean;
    },
    ref,
  ) => {
    if (showCollapse) {
      return (
        <Collapse {...props} ref={ref}>
          {children}
        </Collapse>
      );
    }

    return children;
  },
);
WithCollapse.displayName = 'WithCollapse';

export const ClientInfoSkeleton = () => (
  <Card
    sx={{
      border: '1px solid',
      borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
    }}
  >
    <StyledCardHeader
      title={
        <Stack direction="row" alignItems="center" gap={2}>
          <Skeleton width="40%" />
          <Skeleton variant="circular" width={20} height={20} />
        </Stack>
      }
      action={
        <Stack direction="row" alignItems="center" gap={2}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="circular" width={20} height={20} />
        </Stack>
      }
    />
    <CardContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingBottom: (theme) => theme.utils.important(theme.spacing(2)),
      }}
    >
      {[1, 2, 3, 4].map((item) => (
        <Stack direction="row" alignItems="center" gap={1} key={item}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton width="70%" />
        </Stack>
      ))}
    </CardContent>
  </Card>
);

export const SiteClients = ({ conversation, address, disabled }: RecentConversationsProps) => {
  const { createClientToAddress } = useAddress();

  const { showSuccessAlert } = useNotifications();

  const customViewId = useCustomView(EntityNames.Client)?.customViewId;

  const { navigate } = useNavigateRoute();

  const { containerRef, containerWidth, responsiveCardStyles } = useResponsiveCardStyles();

  const baseFilters: ConditionInput[] = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (address?.addressId) {
      filters.push({
        attribute: 'addresses/addressId',
        operator: '=',
        value: address?.addressId,
      });
    }

    return filters;
  }, [address?.addressId]);

  const [clientSearchTerm, setClientSearchTerm] = useState('');

  const { showModal } = useModalMethods(Modals.AddClientModal);

  const {
    records: clients,
    isLoading: loading,
    isLoadingMore,
    loadMore: clientLoadMore,
    hasMore: clientHasMore,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm: clientSearchTerm,
    filter: baseFilters,
    skip: !address?.addressId,
    showResultsWhenEmptySearchTerm: true,
  }) ?? {};

  const inViewElementRef = useInViewTrigger({
    onEnter: clientLoadMore,
    enabled: !loading && !!clientHasMore && !isLoadingMore,
  });

  const primaryClient = useMemo(
    () => clients?.find((client) => client.clientId === address?.primaryClientId),
    [clients, address?.primaryClientId],
  );

  const sortedClients = useMemo(
    () =>
      [
        primaryClient,
        ...(clients?.filter((client) => client.clientId !== primaryClient?.clientId) ?? []),
      ]
        .filter((client) => !!client)
        // Remove duplicated elements.
        // Operations like Merge, could lead to duplicated elements
        .filter(
          (client, index, arr) =>
            arr.findIndex((c) => c.clientId === client.clientId) === index,
        ),
    [clients, primaryClient],
  );

  useLoadCardCustomAttributeValues({
    sectionKey: 'client',
    entity: 'Client',
    entityIds: sortedClients.map((client) => client.clientId),
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [addClientQuery, setAddClientQuery] = useState('');
  const [isAddClientMenuOpen, setIsAddClientMenuOpen] = useState(false);

  const clientIdsString = sortedClients.map((client) => client.clientId).join(',');

  const partnerIdsString = [address?.partnerId, PLATFORM_PARTNER_ID].join(',');

  const clientsToAddFilters = useMemo(() => {
    return [
      {
        attribute: 'clientId',
        operator: 'not_in',
        value: clientIdsString,
      },
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: partnerIdsString,
      },
    ];
  }, [clientIdsString, partnerIdsString]);

  const {
    records: clientsToAdd,
    loadMore,
    hasMore,
    isLoading,
    isLoadingMore: isLoadingMoreClientsToAdd,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm,
    filter: clientsToAddFilters,
  }) ?? {};

  const filteredClientsToAdd = useMemo(
    () =>
      clientsToAdd
        // Remove duplicated elements
        ?.filter(
          (client, index, arr) =>
            arr.findIndex((c) => c.clientId === client.clientId) === index,
        ) ?? [],
    [clientsToAdd],
  );

  const clientsToAddOptions = useMemo<SelectMenuOption[]>(
    () =>
      filteredClientsToAdd.map((client) => ({
        value: client.clientId,
        title: client.fullName ?? '',
      })),
    [filteredClientsToAdd],
  );

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setClientSearchTerm(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onSelectClientToAdd = useCallback(
    async (clientId: string) => {
      const newValue = filteredClientsToAdd.find((client) => client.clientId === clientId);
      if (!address || !newValue) {
        return;
      }

      const response = await createClientToAddress({
        client: mapClientToAddress(newValue),
        address,
        partnerId: address.partnerId ?? conversation?.partner?.partnerId,
        filters: baseFilters,
        limit: DEFAULT_PAGINATION_LIMIT,
      });

      setSearchTerm('');
      setAddClientQuery('');

      if (response) {
        showSuccessAlert('Client was added to the site');
      }
    },
    [
      address,
      baseFilters,
      conversation?.partner?.partnerId,
      createClientToAddress,
      filteredClientsToAdd,
      showSuccessAlert,
    ],
  );

  const setSearchTermDebounced = useMemo(
    () => debounce(setSearchTerm, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onAddClientQueryChange = useCallback(
    (query: string) => {
      setAddClientQuery(query);
      setSearchTermDebounced(query);
    },
    [setSearchTermDebounced],
  );

  const onCreateClient = useCallback(() => {
    setIsAddClientMenuOpen(false);
    showModal({ address, filters: baseFilters, limit: DEFAULT_PAGINATION_LIMIT });
  }, [address, baseFilters, showModal]);

  const onClientNameClick = useCallback(
    (clientId: string) => {
      navigate(portalPaths.client.full(clientId), { state: { customViewId } });
    },
    [customViewId, navigate],
  );

  const clientsSkeleton = useMemo(() => {
    return Array.from({ length: 2 }, (_, i) => <ClientInfoSkeleton key={i} />);
  }, []);

  return (
    <Stack spacing={1} ref={containerRef} flexGrow={1} minHeight={0}>
      <AutocompleteSearchBar
        disabled={!!address?.isDeleted}
        handleOnFilterResultsChange={handleOnChange}
        addButtonText="+ Add Client"
        addMenu={{
          label: 'Add client',
          options: clientsToAddOptions,
          value: null,
          menuWidth: '20rem',
          open: isAddClientMenuOpen,
          onOpenChange: setIsAddClientMenuOpen,
          search: true,
          searchPlaceholder: 'Find & Associate Existing Client',
          searchValue: addClientQuery,
          onSearchChange: onAddClientQueryChange,
          loading: isLoading,
          // The search is skipped while the query is empty, and a skipped query still reports more pages.
          hasMore: !!hasMore && clientsToAddOptions.length > 0,
          emptyText: addClientQuery ? undefined : 'Type a name to find a client.',
          onLoadMore: loadMore,
          loadingMore: isLoadingMoreClientsToAdd,
          onChange: (clientId) => {
            void onSelectClientToAdd(clientId);
          },
          createAction: { label: 'Create Client', onSelect: onCreateClient },
        }}
      />

      {/* Only render content when we have measured the width */}
      {!isNullish(containerWidth) && (
        <Box display="grid" gap={2} sx={responsiveCardStyles} pb={2}>
          {!!loading && !sortedClients.length && clientsSkeleton}
          {sortedClients?.map((client) => (
            <ClientCard
              key={`client-card-${client.clientId}`}
              clientId={client.clientId}
              conversation={conversation}
              address={address}
              disabled={disabled}
              clientNameTooltipText="Click to open"
              onClientNameClick={onClientNameClick}
            />
          ))}
          {!!isLoadingMore && clientsSkeleton}
          <Box ref={inViewElementRef} sx={{ height: 2 }} />

          {!loading && !sortedClients?.length && (
            <Stack padding="0.5rem">
              <Typography alignSelf="center" variant="body2">
                No results
              </Typography>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
};
